import { Box, Tooltip, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";

type Props = {
	/** Text to collapse and expand. */
	text: string;
	/** Height of the text in collapsed state in px. Default is `48` */
	collapsedHeight?: number;
	/** Animation duration in ms. Default is `300` */
	duration?: number;
};

const ExpandableText = ({
	text,
	collapsedHeight = 48,
	duration = 300,
}: Props) => {
	const [expanded, setExpanded] = useState(false);
	const [contentHeight, setContentHeight] = useState<number | null>(null);
	const [isExpandable, setIsExpandable] = useState(false);
	const contentRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (contentRef.current) {
			const height = contentRef.current.scrollHeight;
			setContentHeight(height);
			setIsExpandable(height > collapsedHeight);
		}
	}, [text, collapsedHeight]);

	const boxProps = {
		sx: {
			cursor: isExpandable ? "pointer" : "default",
			overflow: "hidden",
			height:
				expanded || !isExpandable
					? contentHeight || "auto"
					: collapsedHeight,
			transition: `height ${duration}ms ease`,
		},
		onClick: () => isExpandable && setExpanded((prev) => !prev),
	};

	return (
		<Box {...boxProps}>
			<Tooltip
				title={
					isExpandable
						? expanded
							? "Click to Collapse"
							: "Click to Expand"
						: ""
				}
				disableHoverListener={!isExpandable}
			>
				<Typography
					variant="body2"
					ref={contentRef}
					sx={{
						display: "-webkit-box",
						WebkitBoxOrient: "vertical",
						overflow: "hidden",
						...(expanded || !isExpandable
							? {}
							: {
									WebkitLineClamp: 2,
									textOverflow: "ellipsis",
							  }),
					}}
				>
					{text}
				</Typography>
			</Tooltip>
		</Box>
	);
};

export default ExpandableText;
